// React
import React, { useEffect, useState } from "react";
// MUI
import { Card, Stack, Button, Container, Typography } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Components
import GoInLineTable from "../../../common/GoInLineTable/GoInLineTable";
import CreditNoteLine from "./CreditNoteLine";
// Constants
import PermissionConstants from "../../../configs/PermissionConstants";
// Hooks
import usePermissions from "../../../hooks/usePermissions";
// Utils
import { CREDIT_NOTE_LINE_TABLE_HEADS } from "./CreditNote.utils";
import GoDialog from "../../../common/GoDialog/GoDialog";
// Service
import { del } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
import toast from "react-hot-toast";

import { Form, Formik } from "formik";
import { post, put } from "../../../services/FetchHelper";
import {
  CREDIT_NOTE_LINE_STATE,
  CREDIT_NOTE_LINE_SCHEMA,
} from "./CreditNote.utils";

const CreditNoteLines = ({ creditNoteId, creditNoteLines, reload }) => {
  const spinService = SpinService();
  const permissions = usePermissions();
  const [creditNoteLineState, setCreditNoteLineState] = useState(
    CREDIT_NOTE_LINE_STATE
  );
  const [allCeditNoteLines, setAllCreditNoteLines] = useState([]);
  const [creditNoteLineId, setCreditNoteLineId] = useState(undefined);
  const [confirm, setConfirm] = useState(false);

  const addNew = () => {
    if (
      allCeditNoteLines.find(
        (item) =>
          item.rowAction !== undefined &&
          (item.rowAction === "new" || item.rowAction === "edit")
      ) === undefined
    ) {
      const nr = { ...CREDIT_NOTE_LINE_STATE, rowAction: "new" };
      if (allCeditNoteLines === false) {
        setAllCreditNoteLines([nr]);
      } else {
        setAllCreditNoteLines([...allCeditNoteLines, nr]);
      }
      setCreditNoteLineState(nr);
    }
  };

  const onAction = (item, action) => {
    if (
      action === "edit" &&
      allCeditNoteLines.find(
        (item) =>
          item.rowAction !== undefined &&
          (item.rowAction === "new" || item.rowAction === "edit")
      ) === undefined
    ) {
      setCreditNoteLineId(item.id);
      setCreditNoteLineState(
        allCeditNoteLines.find((row) => row.id === item.id)
      );
      setAllCreditNoteLines((prevFormFields) =>
        prevFormFields.map((row) =>
          row.id === item.id ? { ...row, rowAction: "edit" } : row
        )
      );
    }
    if (action === "delete") {
      setCreditNoteLineId(item.id);
      setConfirm(true);
      return;
    }
  };

  useEffect(() => {
    setAllCreditNoteLines(creditNoteLines);
  }, [creditNoteLines]);

  const onClose = () => {
    reload();
    setCreditNoteLineId(undefined);
  };

  const handleClose = async (res) => {
    if (res) {
      spinService.on();
      const response = await del(
        EntityConstants.CREDIT_NOTE_LINES + `/${creditNoteLineId}`
      );
      if (response.status === 200) {
        toast.success(MessageContants.CREDIT_NOTE_LINE_DELETED);
      }
      reload();
      spinService.off();
    } else {
      setCreditNoteLineId(undefined);
    }
    setConfirm(false);
  };

  const handleSubmit = async (values) => {
    spinService.on();
    const isNewCreditNoteLine = values.id === undefined;
    if (isNewCreditNoteLine) {
      values.credit_note_id = creditNoteId;
    }
    const url = isNewCreditNoteLine
      ? EntityConstants.CREDIT_NOTE_LINES
      : EntityConstants.CREDIT_NOTE_LINES + `/${values.id}`;
    const method = isNewCreditNoteLine ? post : put;
    const response = await method(url, { credit_note_line: values });
    if ([200, 201].indexOf(response.status) > -1) {
      setCreditNoteLineState(CREDIT_NOTE_LINE_STATE);
      toast.success(
        isNewCreditNoteLine
          ? MessageContants.CREDIT_NOTE_LINE_CREATED
          : MessageContants.CREDIT_NOTE_LINE_UPDATED
      );
      onClose();
    }
    spinService.off();
  };

  return (
    <React.Fragment>
      <Container maxWidth={false}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h4" gutterBottom>
            Credit Note Lines
          </Typography>
          {permissions.has(PermissionConstants.CREDIT_NOTE_CREATE) && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={addNew}
            >
              New Line
            </Button>
          )}
        </Stack>
        <Card>
          <Formik
            enableReinitialize
            initialValues={creditNoteLineState}
            validationSchema={CREDIT_NOTE_LINE_SCHEMA}
            onSubmit={(values) => handleSubmit(values)}
          >
            {({ values, touched, errors, handleChange, handleSubmit }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <GoInLineTable
                  tableHeads={CREDIT_NOTE_LINE_TABLE_HEADS}
                  tableDatas={allCeditNoteLines}
                  onAction={(row, action) => onAction(row, action)}
                  editRowId={creditNoteLineId}
                  formElement={
                    <CreditNoteLine
                      values={values}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      creditNoteId={creditNoteId}
                      creditNoteLineId={creditNoteLineId}
                      onClose={() => onClose()}
                    />
                  }
                />
              </Form>
            )}
          </Formik>
        </Card>
      </Container>
      <GoDialog
        open={confirm}
        title=""
        content="Are you sure you want to delete?"
        positive="Yes"
        negative="No"
        handleClose={(res) => handleClose(res)}
      />
    </React.Fragment>
  );
};

export default CreditNoteLines;
